import React, { useEffect } from 'react';
import clsx from 'clsx';
import { SelectValue } from 'antd/lib/select';
import { isNotNullable } from 'src/utils/typeGuards';
import useQueryUrlParams from 'src/hooks/useQueryUrlParams';
import { useCustomFilterMatches } from 'src/components/ProductDetails/CustomFilterMatches/hooks';
import { ProductDetailsState } from 'src/stateProviders/productDetailsStateProvider';
import { useProductDetailsStateContext } from 'src/stateProviders/useProductDetailsStateContext';
import VarianceFilter from './VarianceFilter';
import OwnerFilter from './OwnerFilter';
import PriceTypeFilter from './PriceTypeFilter';
import MatchTypeFilter from './MatchTypeFilter';
import useSourceOwnerId from 'src/hooks/useSourceOwnerId';
import RegionFilter from './RegionFilter/RegionFilter';
import ProductFinderQueryParams from 'src/types/ProductFinderQueryParams';
import useQueryUrlParamsDispatch from 'src/hooks/useQueryUrlParamsDispatch';
import styles from './OptionsFiltersSider.module.less';

type OptionsFiltersSiderProps = {
  className?: string;
  collapsedOptions?: boolean;
  selectedItems?: SelectValue;
  isBackResults?: boolean;
  onSelectedOptionsCount?: (count: number) => void;
};

const OptionsFiltersSider: React.FC<OptionsFiltersSiderProps> = ({
  collapsedOptions,
  selectedItems,
  isBackResults,
  onSelectedOptionsCount,
}) => {
  const sourceOwnerId = useSourceOwnerId();
  const { productId = '' }: Partial<ProductDetailsState> = useProductDetailsStateContext();
  const { data, loading, error } = useCustomFilterMatches(sourceOwnerId, productId);
  const { matchTypeFilter, region, priceType, owner, variance } = useQueryUrlParams();
  const setQuery = useQueryUrlParamsDispatch<ProductFinderQueryParams>();
  const selectedOptionsCount = [region, priceType, matchTypeFilter, owner, variance].filter(isNotNullable).length;

  useEffect(() => {
    if (onSelectedOptionsCount) {
      onSelectedOptionsCount(selectedOptionsCount);
    }
  }, [selectedOptionsCount]);

  const handleFiltersUpdate = (key: string, value: string) => {
    setQuery({
      [key]: value,
      category: [],
    });
  };

  return (
    <div
      className={clsx(styles.filterContainer, 'widget_color', collapsedOptions || styles.collapsed, {
        [styles.isBackResults]: isBackResults,
        [styles.productDetails]: !!productId,
      })}
    >
      <div className={clsx(isNotNullable(region) && styles.filterSelected)}>
        <RegionFilter
          sourceOwnerId={sourceOwnerId}
          selectedItems={selectedItems}
          onChange={handleFiltersUpdate}
          selectedSites={region}
          defaultValue={region}
        />
      </div>
      <div className={clsx(isNotNullable(priceType) && styles.filterSelected)}>
        <PriceTypeFilter />
      </div>
      <div className={clsx(isNotNullable(matchTypeFilter) && styles.filterSelected)}>
        <MatchTypeFilter />
      </div>
      {productId && (
        <>
          <div className={clsx(isNotNullable(owner) && styles.filterSelected)}>
            <OwnerFilter data={data?.owners ?? null} loading={loading} error={error} />
          </div>
          <div className={clsx(isNotNullable(variance) && styles.filterSelected)}>
            <VarianceFilter data={data?.variance ?? null} loading={loading} error={error} />
          </div>
        </>
      )}
    </div>
  );
};

export default OptionsFiltersSider;
